import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

export default function ReviewClaims() {
  const [token, setToken] = useState();
  const [claims, setClaims] = useState([]);
  const [madeByMeClaims, setMadeByMeClaims] = useState([]);
  const [actionError, setActionError] = useState("");
  const [itemNameTranslations, setItemNameTranslations] = useState({});
  const navigate = useNavigate();

  const translateItemName = (rawName) => itemNameTranslations[rawName] || rawName;

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (!fetchedToken) {
        navigate("/login");
        return;
      }
      setToken(fetchedToken);

      let languageId = 1;
      if (fetchedToken?.account?.username) {
        const accountRes = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(fetchedToken.account.username)}`
        );
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          languageId = accountData.language_id || 1;
        }
      }
      try {
        const itemsRes = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/translations/lookup/items?language_id=${languageId}`
        );
        if (itemsRes.ok) setItemNameTranslations(await itemsRes.json());
      } catch {}
    };
    init();
  }, []);

  const loadClaims = async (currentToken) => {
    const headers = { Authorization: `Bearer ${currentToken.access_token}` };
    const response = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/claims/pending`,
      { headers }
    );
    if (response.ok) {
      setClaims(await response.json());
    }

    const madeByMeResponse = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/claims/made-by-me`,
      { headers }
    );
    if (madeByMeResponse.ok) {
      setMadeByMeClaims(await madeByMeResponse.json());
    }
  };

  useEffect(() => {
    if (token) loadClaims(token);
  }, [token]);

  const handleApprove = async (claim) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claim.id}/approve`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not approve claim");
      }
      await loadClaims(token);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleReject = async (claim) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claim.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not reject claim");
      }
      await loadClaims(token);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleCancelOwnClaim = async (claim) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claim.id}/cancel`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not cancel claim");
      }
      await loadClaims(token);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleApproveAll = async (claimedByAccountId) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/approve-all?claimed_by=${claimedByAccountId}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not approve claims");
      }
      await loadClaims(token);
    } catch (error) {
      setActionError(error.message);
    }
  };

  if (!token) {
    return null;
  }

  const pendingCountByClaimer = claims.reduce((acc, claim) => {
    acc[claim.claimed_by_account_id] = acc[claim.claimed_by_account_id] || {
      username: claim.claimed_by_username,
      count: 0,
    };
    acc[claim.claimed_by_account_id].count += 1;
    return acc;
  }, {});

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Claims</h1>

        <h2 className="text-lg font-bold mb-2">Claims For Me</h2>

        {Object.keys(pendingCountByClaimer).length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold mb-2">Bulk approve by claimer</h2>
            {Object.entries(pendingCountByClaimer).map(([claimerId, info]) => (
              <button
                key={claimerId}
                onClick={() => handleApproveAll(claimerId)}
                className="mr-2 mb-2 p-1 border rounded"
              >
                Approve all {info.count} from {info.username}
              </button>
            ))}
          </div>
        )}

        {claims.length === 0 ? (
          <p>No pending claims.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-left">
            <thead>
              <tr>
                <th className="pr-4">Item</th>
                <th className="pr-4">Claimed By</th>
                <th className="pr-4">Submitted At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => (
                <tr key={claim.id}>
                  <td className="pr-4 py-1">{translateItemName(claim.item_name)}</td>
                  <td className="pr-4 py-1">{claim.claimed_by_username}</td>
                  <td className="pr-4 py-1">
                    {new Date(claim.created_at).toLocaleString()}
                  </td>
                  <td className="py-1">
                    <button
                      onClick={() => handleApprove(claim)}
                      className="mr-2 p-1 border rounded"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(claim)}
                      className="p-1 border rounded"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h2 className="text-lg font-bold mb-2 mt-8">Claims I've Made</h2>
        <p className="text-gray-400 text-sm mb-4">
          Items you've marked as found on a friend's behalf, still waiting on
          their approval. Sent the wrong one by accident? Cancel it here
          before they review it -- only they can approve or reject a claim,
          but you can always pull back your own submission first.
        </p>

        {madeByMeClaims.length === 0 ? (
          <p>You haven't claimed anything for anyone yet.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-left">
            <thead>
              <tr>
                <th className="pr-4">Item</th>
                <th className="pr-4">Claimed For</th>
                <th className="pr-4">Submitted At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {madeByMeClaims.map((claim) => (
                <tr key={claim.id}>
                  <td className="pr-4 py-1">{translateItemName(claim.item_name)}</td>
                  <td className="pr-4 py-1">{claim.claimed_for_username}</td>
                  <td className="pr-4 py-1">
                    {new Date(claim.created_at).toLocaleString()}
                  </td>
                  <td className="py-1">
                    <button
                      onClick={() => handleCancelOwnClaim(claim)}
                      className="p-1 border rounded"
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {actionError && <p className="text-red-500 mt-2">{actionError}</p>}
      </div>
    </div>
  );
}
